import { ArrowRightIcon, InformationCircleIcon } from '@heroicons/react/20/solid'
import { motion } from 'motion/react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocale } from '../../../hooks/helpers'

type FunnelStage = {
	label: string
	value: number
	color: string
	bgColor: string
	borderColor: string
	description: string
}

type CampaignFunnelVisualizationProps = {
	impressions: number | null
	reach: number | null
	clicks: number | null
	applications: number
	isLoadingMeta?: boolean
}

export function CampaignFunnelVisualization({
	impressions,
	reach,
	clicks,
	applications,
	isLoadingMeta,
}: CampaignFunnelVisualizationProps) {
	const { t } = useTranslation('analytics')

	if (isLoadingMeta) {
		return (
			<div className="relative overflow-hidden p-6 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm">
				<div className="relative">
					<div className="mb-4">
						<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-2">
							{t('funnel.title')}
						</h2>
						<p className="text-sm text-grey-600">
							{t('funnel.loading')}
						</p>
					</div>
					<div className="h-96 flex items-center justify-center">
						<div className="text-sm text-grey-500">{t('funnel.loadingLong')}</div>
					</div>
				</div>
			</div>
		)
	}

	const stages: FunnelStage[] = []

	if (impressions !== null) {
		stages.push({
			label: t('funnel.impressions'),
			value: impressions,
			color: 'text-indigo-700',
			bgColor: 'bg-indigo-50',
			borderColor: 'border-indigo-200',
			description: t('funnel.impressionsDesc'),
		})
	}

	if (reach !== null) {
		stages.push({
			label: t('funnel.reach'),
			value: reach,
			color: 'text-cyan-700',
			bgColor: 'bg-cyan-50',
			borderColor: 'border-cyan-200',
			description: t('funnel.reachDesc'),
		})
	}

	if (clicks !== null) {
		stages.push({
			label: t('funnel.clicks'),
			value: clicks,
			color: 'text-orange-700',
			bgColor: 'bg-orange-50',
			borderColor: 'border-orange-200',
			description: t('funnel.clicksDesc'),
		})
	}

	stages.push({
		label: t('funnel.applications'),
		value: applications,
		color: 'text-purple-700',
		bgColor: 'bg-purple-50',
		borderColor: 'border-purple-200',
		description: t('funnel.applicationsDesc'),
	})

	const getConversionRate = (current: number | null, previous: number | null): string => {
		if (current === null || previous === null) { return 'N/A' }
		if (previous === 0) { return 'N/A' }
		return `${((current / previous) * 100).toFixed(1)}%`
	}

	return (
		<div className="relative overflow-hidden p-8 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm">
			<div className="relative">
				<div className="mb-6">
					<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-2">
						{t('funnel.title')}
					</h2>
					<p className="text-sm text-grey-600">
						{t('funnel.subtitle')}
					</p>
				</div>

				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.2, delay: 0.1 }}
					className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8"
				>
					<StatCard
						label={t('funnel.totalReach')}
						value={formatMetricValue(impressions)}
						subtext={t('funnel.totalReachSubtext')}
					/>
					<StatCard
						label={t('funnel.clickThroughRate')}
						value={getConversionRate(clicks, impressions)}
						subtext={t('funnel.clickThroughRateSubtext')}
					/>
					<StatCard
						label={t('funnel.applicationRate')}
						value={getConversionRate(applications, clicks)}
						subtext={t('funnel.applicationRateSubtext')}
					/>
				</motion.div>

				<div className="flex items-stretch gap-4">
					{stages.map((stage, index) => {
						const conversionRate = index > 0
							? getConversionRate(stage.value, stages[index - 1].value)
							: null

						return (
							<Fragment key={stage.label}>
								<motion.div
									initial={{ opacity: 0, scale: 0.9 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
									className="relative flex-1 h-[200px]"
								>
									<div
										className={`relative ${stage.bgColor} ${stage.borderColor} border-2 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-md h-full flex items-center justify-center`}
									>
										<div className="p-6 w-full">
											<div className="text-center">
												<div className="flex items-center justify-center gap-2 mb-2">
													<h3
														className={`text-base font-bold ${stage.color}`}
													>
														{stage.label}
													</h3>
												</div>
												<div className={`text-3xl font-bold ${stage.color} mb-2`}>
													{formatNumber(stage.value)}
												</div>
												<p className="text-xs text-grey-600 mb-3">
													{stage.description}
												</p>
												{conversionRate && (
													<div className="text-xs font-semibold text-grey-600 bg-white/80 px-2 py-1 rounded inline-block">
														{conversionRate} {t('funnel.converted')}
													</div>
												)}
											</div>
										</div>
									</div>
								</motion.div>

								{index < stages.length - 1 && (
									<motion.div
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
										className="flex flex-col items-center justify-center gap-1 flex-shrink-0 px-2"
									>
										<ArrowRightIcon className="w-8 h-8 text-grey-400" />
										<div className="text-xs font-semibold text-grey-600 bg-white px-2 py-1 rounded-full shadow-sm border border-slate-200 whitespace-nowrap">
											-{formatNumber(stage.value - stages[index + 1].value)}
										</div>
									</motion.div>
								)}
							</Fragment>
						)
					})}
				</div>

				{stages.length >= 3 && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.3, delay: 0.8 }}
						className="mt-8 p-5 bg-blue-50/50 border-2 border-blue-200 rounded-lg"
					>
						<h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
							<InformationCircleIcon className="w-4 h-4" />
							{t('funnel.keyInsight')}
						</h3>
						<p className="text-sm text-blue-800">
							{getKeyInsight(stages, impressions, clicks, applications, t)}
						</p>
					</motion.div>
				)}
			</div>
		</div>
	)
}

function StatCard({
	label,
	value,
	subtext,
}: {
	label: string
	value: string
	subtext: string
}) {
	return (
		<div className="p-4 rounded-lg bg-white/70 border border-slate-200/70 shadow-sm">
			<div className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-1">
				{label}
			</div>
			<div className="text-2xl font-bold text-grey-900 mb-1">{value}</div>
			<div className="text-xs text-grey-600">{subtext}</div>
		</div>
	)
}

function formatNumber(num: number): string {
	return new Intl.NumberFormat(getLocale(), {
		notation: 'compact',
		compactDisplay: 'short',
		maximumFractionDigits: 1,
	}).format(num)
}

function formatMetricValue(value: number | null): string {
	if (value === null) { return 'N/A' }
	return formatNumber(value)
}

function getKeyInsight(
	stages: FunnelStage[],
	impressions: number | null,
	clicks: number | null,
	applications: number,
	t: (key: string, options?: Record<string, string>) => string,
): string {
	const clickRate = impressions !== null && clicks !== null && impressions > 0
		? (clicks / impressions) * 100
		: null

	const appRate = clicks !== null && clicks > 0 ? (applications / clicks) * 100 : null

	const rates = [
		clickRate !== null ? { stage: 'click' as const, rate: clickRate } : null,
		appRate !== null ? { stage: 'application' as const, rate: appRate } : null,
	].filter((r): r is { stage: 'click' | 'application'; rate: number } => r !== null && r.rate > 0)

	if (rates.length === 0) {
		return t('funnel.insightNoData')
	}

	const weakest = rates.reduce((min, r) => (r.rate < min.rate ? r : min))

	if (weakest.stage === 'click' && clickRate !== null && clickRate < 2) {
		return t('funnel.insightLowClickRate', { rate: clickRate.toFixed(1) })
	}

	if (weakest.stage === 'application' && appRate !== null && appRate < 10) {
		return t('funnel.insightLowAppRate', { rate: appRate.toFixed(1) })
	}

	return t('funnel.insightPerforming')
}
